import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import {
  Ticket,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Percent,
  Calendar,
  Users,
  CheckCircle,
  XCircle
} from 'lucide-react'

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState(null)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: 50,
    duration: 'repeating',
    durationMonths: 3,
    maxRedemptions: 100,
    validUntil: '',
    newCustomersOnly: true,
    active: true
  })

  useEffect(() => {
    loadCoupons()
  }, [])

  const loadCoupons = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getCoupons()
      setCoupons(response.data.data.coupons)
    } catch (err) {
      console.error('Error loading coupons:', err)
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingCoupon(null)
    setForm({
      code: '',
      name: '',
      description: '',
      discountType: 'percentage',
      discountValue: 50,
      duration: 'repeating',
      durationMonths: 3,
      maxRedemptions: 100,
      validUntil: '',
      newCustomersOnly: true,
      active: true
    })
    setShowModal(true)
  }

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon)
    setForm({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      duration: coupon.duration,
      durationMonths: coupon.durationMonths || 3,
      maxRedemptions: coupon.maxRedemptions || '',
      validUntil: coupon.validUntil ? coupon.validUntil.split('T')[0] : '',
      newCustomersOnly: coupon.newCustomersOnly,
      active: coupon.active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      if (editingCoupon) {
        await adminApi.updateCoupon(editingCoupon._id, form)
      } else {
        await adminApi.createCoupon(form)
      }
      setShowModal(false)
      loadCoupons()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Disattivare questo coupon?')) return
    try {
      await adminApi.deleteCoupon(id)
      loadCoupons()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    }
  }

  const formatDiscount = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}%`
    } else if (coupon.discountType === 'fixed') {
      return `€${(coupon.discountValue / 100).toFixed(2)}`
    } else {
      return `${coupon.discountValue} mesi gratis`
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupon</h1>
          <p className="text-gray-500">Gestisci codici sconto e promozioni</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Coupon
        </button>
      </div>

      {/* Coupons List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {coupons.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Ticket className="h-12 w-12 mb-4 text-gray-300" />
            <p>Nessun coupon creato</p>
          </div>
        ) : (
          <div className="divide-y">
            {coupons.map((coupon) => (
              <div key={coupon._id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl font-mono font-bold text-primary-600">
                        {coupon.code}
                      </span>
                      {coupon.active ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Attivo
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full flex items-center">
                          <XCircle className="h-3 w-3 mr-1" />
                          Disattivato
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-gray-900 font-medium">{coupon.name}</p>
                    {coupon.description && (
                      <p className="text-sm text-gray-500">{coupon.description}</p>
                    )}
                    
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Percent className="h-4 w-4 mr-1" />
                        {formatDiscount(coupon)}
                        {coupon.duration === 'repeating' && ` per ${coupon.durationMonths} mesi`}
                        {coupon.duration === 'forever' && ' per sempre'}
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {coupon.timesRedeemed || 0} / {coupon.maxRedemptions || '∞'} utilizzi
                      </div>
                      {coupon.validUntil && (
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Scade: {new Date(coupon.validUntil).toLocaleDateString('it-IT')}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(coupon)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(coupon._id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingCoupon ? 'Modifica Coupon' : 'Nuovo Coupon'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Codice
                  </label>
                  <input
                    type="text"
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border rounded-lg uppercase"
                    placeholder="LANCIO2025"
                    disabled={!!editingCoupon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Lancio 2025"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="50% di sconto per i primi 3 mesi"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo Sconto
                  </label>
                  <select
                    value={form.discountType}
                    onChange={(e) => setForm({ ...form, discountType: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="percentage">Percentuale (%)</option>
                    <option value="fixed">Importo Fisso (€)</option>
                    <option value="free_months">Mesi Gratis</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valore
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={form.discountValue}
                    onChange={(e) => setForm({ ...form, discountValue: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durata
                  </label>
                  <select
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="once">Una volta</option>
                    <option value="repeating">Ripetuto (mesi)</option>
                    <option value="forever">Per sempre</option>
                  </select>
                </div>
                {form.duration === 'repeating' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mesi
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={form.durationMonths}
                      onChange={(e) => setForm({ ...form, durationMonths: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Utilizzi
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.maxRedemptions}
                    onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value ? parseInt(e.target.value) : '' })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Illimitati"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scadenza
                  </label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={form.newCustomersOnly}
                    onChange={(e) => setForm({ ...form, newCustomersOnly: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Solo nuovi clienti</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Attivo</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {saving ? 'Salvataggio...' : editingCoupon ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
